import {
  leadIntakeAddressResponseSchema,
  leadIntakeMetaPagesResponseSchema,
  leadIntakeRecentResponseSchema,
  leadIntakeSettingsResponseSchema,
  leadIntakeTestEntryResponseSchema,
  type LeadIntakeDoor,
  type LeadIntakeMetaConnectBody,
  type SaveLeadIntakeSettingsBody,
} from "@gioia365/contracts";
import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "../../api/http";
import { tabKey, type TabContext } from "../../session/tab-context";

// Le richieste di Impostazioni → «Ingressi dei Lead» (piano dell'ingresso del 10 ottobre 2026, I2, Task 4): le porte, il loro
// salvataggio, «Copia l'indirizzo», «Cambia l'indirizzo», «Fai entrare un Lead di prova» e «Gli ultimi Lead arrivati», con le chiavi
// dell'azienda della scheda (`["scheda", tabKey(tab), …]`); dal piano del 12 ottobre 2026 (I3) le pagine di Facebook, «Collega la
// pagina» e «Scollega la pagina» dei moduli di Meta.

/** Ogni quanto «Gli ultimi Lead arrivati» si rilegge da sola, finché la pagina è aperta. */
const RECENT_REFRESH_MS = 30_000;

/** Le porte dell'azienda (`GET /api/settings/lead-intake`), con l'indirizzo nascosto e se chi guarda le può cambiare. */
export function leadIntakeSettingsQuery(tab: TabContext) {
  return queryOptions({
    queryKey: ["scheda", tabKey(tab), "lead-intake"],
    queryFn: ({ signal }) => apiRequest("GET", "/api/settings/lead-intake", { schema: leadIntakeSettingsResponseSchema, tab, signal }),
  });
}

/** «Gli ultimi Lead arrivati» (`GET …/recent`): soltanto per chi imposta gli ingressi, riletti ogni 30 secondi. */
export function leadIntakeRecentQuery(tab: TabContext, enabled: boolean) {
  return queryOptions({
    queryKey: ["scheda", tabKey(tab), "lead-intake", "recent"],
    queryFn: ({ signal }) => apiRequest("GET", "/api/settings/lead-intake/recent", { schema: leadIntakeRecentResponseSchema, tab, signal }),
    enabled,
    refetchInterval: RECENT_REFRESH_MS,
  });
}

/** Salva le due porte (`PUT /api/settings/lead-intake`): la risposta prende il posto della copia; dopo un rifiuto si rileggono. */
export function useSaveLeadIntakeSettings(tab: TabContext) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: SaveLeadIntakeSettingsBody) => apiRequest("PUT", "/api/settings/lead-intake", { schema: leadIntakeSettingsResponseSchema, body, tab }),
    onSuccess: (response) => queryClient.setQueryData(leadIntakeSettingsQuery(tab).queryKey, response),
    onError: () => void queryClient.invalidateQueries({ queryKey: leadIntakeSettingsQuery(tab).queryKey, exact: true }),
  });
}

/** «Copia l'indirizzo» (`POST …/copy-address`): l'indirizzo intero, che il server registra come copiato. */
export function useCopyLeadIntakeAddress(tab: TabContext) {
  return useMutation({
    mutationFn: (door: LeadIntakeDoor) => apiRequest("POST", "/api/settings/lead-intake/copy-address", { schema: leadIntakeAddressResponseSchema, body: { door }, tab }),
  });
}

/** «Cambia l'indirizzo» (`POST …/rotate`): la chiave nuova; la risposta sono le porte di adesso. */
export function useRotateLeadIntakeAddress(tab: TabContext) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (door: LeadIntakeDoor) => apiRequest("POST", "/api/settings/lead-intake/rotate", { schema: leadIntakeSettingsResponseSchema, body: { door }, tab }),
    onSuccess: (response) => queryClient.setQueryData(leadIntakeSettingsQuery(tab).queryKey, response),
  });
}

/** «Fai entrare un Lead di prova» (`POST …/test-entry`): poi «Gli ultimi Lead arrivati» si rilegge. */
export function useLeadIntakeTestEntry(tab: TabContext) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiRequest("POST", "/api/settings/lead-intake/test-entry", { schema: leadIntakeTestEntryResponseSchema, body: {}, tab }),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: leadIntakeRecentQuery(tab, true).queryKey }),
  });
}

/** Le pagine Facebook di chi è entrato con Facebook (`POST …/meta/pages`): il gettone breve va al server, le pagine tornano senza. */
export function useLeadIntakeMetaPages(tab: TabContext) {
  return useMutation({
    mutationFn: (accessToken: string) => apiRequest("POST", "/api/settings/lead-intake/meta/pages", { schema: leadIntakeMetaPagesResponseSchema, body: { accessToken }, tab }),
  });
}

/** «Collega questa pagina» (`POST …/meta/connect`): la risposta sono le porte di adesso; dopo un rifiuto si rileggono. */
export function useConnectLeadIntakeMeta(tab: TabContext) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: LeadIntakeMetaConnectBody) => apiRequest("POST", "/api/settings/lead-intake/meta/connect", { schema: leadIntakeSettingsResponseSchema, body, tab }),
    onSuccess: (response) => queryClient.setQueryData(leadIntakeSettingsQuery(tab).queryKey, response),
    onError: () => void queryClient.invalidateQueries({ queryKey: leadIntakeSettingsQuery(tab).queryKey, exact: true }),
  });
}

/** «Scollega la pagina» (`POST …/meta/disconnect`), con la versione letta. */
export function useDisconnectLeadIntakeMeta(tab: TabContext) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (expectedVersion: number) => apiRequest("POST", "/api/settings/lead-intake/meta/disconnect", { schema: leadIntakeSettingsResponseSchema, body: { expectedVersion }, tab }),
    onSuccess: (response) => queryClient.setQueryData(leadIntakeSettingsQuery(tab).queryKey, response),
    onError: () => void queryClient.invalidateQueries({ queryKey: leadIntakeSettingsQuery(tab).queryKey, exact: true }),
  });
}
